/** Battery at or below this level (and unplugged) → instant tab pager. */
export const TAB_PAGER_LITE_BATTERY_LEVEL = 0.2;

export type TabPagerLiteReason = 'battery' | 'save-data';

let liteModeActive = false;
let liteReason: TabPagerLiteReason | null = null;

export function isTabPagerLiteMode(): boolean {
  return liteModeActive;
}

export function tabPagerLiteReason(): TabPagerLiteReason | null {
  return liteReason;
}

export function enableTabPagerLiteMode(reason: TabPagerLiteReason): void {
  if (liteModeActive) {
    return;
  }
  liteModeActive = true;
  liteReason = reason;
}

type BatteryManagerLike = {
  charging: boolean;
  level: number;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
};

type NavigatorWithBattery = Navigator & {
  getBattery?: () => Promise<BatteryManagerLike>;
  connection?: {
    saveData?: boolean;
    addEventListener?: (type: string, listener: () => void) => void;
    removeEventListener?: (type: string, listener: () => void) => void;
  };
};

function isLowBattery(battery: BatteryManagerLike): boolean {
  return !battery.charging && battery.level <= TAB_PAGER_LITE_BATTERY_LEVEL;
}

/**
 * Watches battery (where supported) and Save-Data. Returns a dispose function.
 * iOS Low Power Mode is not exposed to the web — no heuristic jank detection here
 * (gesture event timing is too noisy on real devices and caused false degrades).
 */
export function initTabPagerPerformanceWatch(
  onLiteMode?: () => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const cleanups: Array<() => void> = [];
  const notifyLite = () => {
    if (liteModeActive) {
      onLiteMode?.();
    }
  };

  const nav = navigator as NavigatorWithBattery;
  if (nav.connection?.saveData) {
    enableTabPagerLiteMode('save-data');
    notifyLite();
  }
  if (nav.connection?.addEventListener) {
    const onConnectionChange = () => {
      if (nav.connection?.saveData) {
        enableTabPagerLiteMode('save-data');
        notifyLite();
      }
    };
    nav.connection.addEventListener('change', onConnectionChange);
    cleanups.push(() =>
      nav.connection?.removeEventListener?.('change', onConnectionChange),
    );
  }

  void (async () => {
    if (!nav.getBattery) {
      return;
    }
    try {
      const battery = await nav.getBattery();
      const onBatteryChange = () => {
        if (isLowBattery(battery)) {
          enableTabPagerLiteMode('battery');
          notifyLite();
        }
      };
      onBatteryChange();
      battery.addEventListener('levelchange', onBatteryChange);
      battery.addEventListener('chargingchange', onBatteryChange);
      cleanups.push(() => {
        battery.removeEventListener('levelchange', onBatteryChange);
        battery.removeEventListener('chargingchange', onBatteryChange);
      });
    } catch {
      // Battery Status API unavailable (e.g. iOS Safari).
    }
  })();

  return () => {
    for (const cleanup of cleanups) {
      cleanup();
    }
  };
}
